import React from "react";
import { Link, Redirect, useLocation, useParams } from "react-router-dom";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import cx from "classnames";
import { useForm } from "react-hook-form";
import type { FieldError, UseFormRegisterReturn } from "react-hook-form";
// eslint-disable-next-line no-restricted-imports
import { Footer } from "../components/Footer/FooterV2";
// eslint-disable-next-line no-restricted-imports
import { LoadingToast } from "../AppointmentReview/LoadingToast";
import { WelcomeBack } from "./WelcomeBack";
import { WeekTimeSlots } from "./WeekTimeSlots";
import { OfficeCard } from "./OfficeCard";
import { TrustBuildCard } from "./TrustBuildCard";
import { NearbyOffice } from "./NearbyOffice";
import { DoctorCard } from "./DoctorCard";
import styles from "./KnownPatientPage.module.scss";
import startStyles from "./AppointmentBookingStartV3.module.scss";
import {
  getKnownPatientRequest,
  KnownPatient,
} from "api/getKnownPatientRequest";
import { getLastAppointmentRequest } from "api/getCurrentAppointmentRequest";
import { Button, ButtonRole } from "components/Button";
import { Navbar } from "components/Navbar";
import { ReviewModal } from "components/Modal";
import { SmsCheckbox } from "components/SmsCheckbox";
import { maskDate } from "components/MaskedTextInput/maskDate";
import { maskPhone } from "components/MaskedTextInput/maskPhone";
import { Layout } from "features/layout";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { setAppointmentData } from "features/appointmentDataSlice";
import * as paths from "features/routing/paths";
import { knownPatientSlotTakenState } from "features/scheduling/AppointmentSlotTaken/locationState";
import { WithOffice } from "components/WithOffice";
import { WithOfficeDoctors } from "components/WithOfficeDoctors";
import { selectOffice } from "features/officesSlice";
import { useOffice } from "features/patientDashboard/hooks/useOffice";
import { AppDispatch, RootState } from "app/store";
import { API_DATE_FORMAT } from "api/constants";
import { useQueryParam } from "hooks/useQueryParam";
import { useTrackEvent } from "hooks/useTrackEvent";
import { ExamType } from "types/examType";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { Slot } from "types/slot";
import { Office } from "types/office";
import { Doctor } from "types/doctor";
import {
  fetchNearbyOffices,
  selectNearbyOffices,
  selectNearbyOfficeAvailabilitySlots,
  selectNearbyOfficeLoadingState,
} from "features/scheduling/nearbyOfficesSlice";
import {
  fetchOfficeAvailability,
  setOfficeAvailability,
} from "features/scheduling/availabilitySummarySlice";
import {
  selectIstimeSlotLoaded,
  selectTimeslots,
  setTimeSlots,
} from "features/scheduling/timeSlotsSlice";
import { getSlotMetaData } from "api/getSlotMetaData";
import {
  createAppointment,
  CreateAppointmentResult,
  resetAppointmentBooked,
  selectAppointmentBookedStatus,
} from "features/scheduling/appointmentCreationSlice";
import { doctorFullName, doctorDiscountNameVEH } from "utils/doctorFullName";
import {
  acceptableCharacters,
  isEmail,
  isValidTLDLength,
} from "utils/emailValidation";
import { isValidDateOfBirth } from "utils/dateOfBirthValidation";
import { isValidDateOfBirthVEH } from "utils/dateOfBirthValidationVEH";
import { isValidDOBRestrictVEH } from "utils/isValidDOBRestrictVEH";
import { isValidPhoneNumber } from "utils/phoneNumberValidation";
import { presenceNameValidation } from "utils/presenceNameValidation";
import { sanitizePatientNameInput } from "utils/sanitizePatientNameInput";
import { areExtendedPatientNameFieldsEnabled } from "featureFlags/areExtendedPatientNameFieldsEnabled";
import { CurrentAppointment } from "types/currentAppointment";
import formInputStyles from "components/TextInput/TextInputV2.module.scss";
import calendarIcon from "images/calendar.svg";
import clockIcon from "images/clockAfternoon.svg";
import closeIcon from "images/close.svg";
import editIcon from "images/edit-outline.svg";
import errorIcon from "images/error.svg";
import locationIcon from "images/pin-drop.svg";
import doctorPlaceholder from "images/doctor-placeholder.svg";

type KnownPatientPageStatus = "loading" | "resolved" | "empty" | "error";

function useScreenSize() {
  const [screenSize, setScreenSize] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
}

const fallbackKnownPatient = (
  patient: Partial<KnownPatient>
): KnownPatient => ({
  id: patient.id || "",
  firstName: patient.firstName || "",
  lastName: patient.lastName || "",
  phoneNumber: patient.phoneNumber || "",
  email: patient.email || "",
  dateOfBirth: patient.dateOfBirth || "",
  smsOptInNotices: patient.smsOptInNotices ?? true,
  smsOptInPromos: patient.smsOptInPromos ?? true,
  lastAppointment: patient.lastAppointment || null,
});

const appointmentToKnownPatientLastAppointment = (
  appointment: CurrentAppointment | null | undefined
): KnownPatient["lastAppointment"] => {
  if (!appointment) return null;

  return {
    date: appointment.date,
    doctorFirstName: appointment.doctor.firstName,
    doctorLastName: appointment.doctor.lastName,
    doctorProviderSuffix: appointment.doctor.providerSuffix,
    officeId: appointment.officeId,
  };
};

const KnownPatientScheduleLoading: React.FC = () => (
  <div className={styles.knownPatientSchedule}>
    <div className={styles.scheduleShell}>
      <div className={startStyles.firstContainer}>
        <section
          aria-label="Loading doctors and appointment times"
          className={styles.loadingScheduleColumn}
          role="status"
        >
          <span className={styles.screenReaderOnly}>
            Loading doctors and appointment times...
          </span>
          <div className={styles.loadingHeadline} />
          <div className={styles.loadingDivider} />
          <div className={styles.loadingDateRow}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                className={styles.loadingDateCard}
                key={`known-patient-loading-date-${index}`}
              />
            ))}
          </div>
          <div className={styles.loadingDoctorCard}>
            <div className={styles.loadingAvatar} />
            <div className={styles.loadingDoctorDetails}>
              <div className={styles.loadingLine} />
              <div className={styles.loadingLineShort} />
            </div>
            <div className={styles.loadingSlots}>
              {Array.from({ length: 12 }).map((_, index) => (
                <div
                  className={styles.loadingSlot}
                  key={`known-patient-loading-slot-${index}`}
                />
              ))}
            </div>
          </div>
          <div className={styles.loadingDoctorCard}>
            <div className={styles.loadingAvatar} />
            <div className={styles.loadingDoctorDetails}>
              <div className={styles.loadingLine} />
              <div className={styles.loadingLineShort} />
            </div>
            <div className={styles.loadingSlots}>
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  className={styles.loadingSlot}
                  key={`known-patient-loading-slot-second-${index}`}
                />
              ))}
            </div>
          </div>
        </section>
        <aside className={startStyles.rightContainer}>
          <div className={styles.loadingRailCard} />
          <div className={styles.loadingRailCard} />
        </aside>
      </div>
    </div>
  </div>
);

interface KnownPatientSchedulerProps {
  doctors: Doctor[];
  office: Office;
  patient: KnownPatient;
}

type KnownPatientFormDetails = Pick<
  KnownPatient,
  "firstName" | "lastName" | "dateOfBirth" | "phoneNumber" | "email"
>;

const formatDateOfBirthForInput = (dateOfBirth: string) => {
  if (!dateOfBirth) return "";

  const parsedDate = dayjs(dateOfBirth);
  return parsedDate.isValid()
    ? parsedDate.format("MM/DD/YYYY")
    : maskDate(dateOfBirth);
};

const formatDateOfBirthForBooking = (dateOfBirth: string) => {
  const [month, day, year] = dateOfBirth.split("/");
  return month && day && year ? `${year}-${month}-${day}` : dateOfBirth;
};

const formatDateOfBirthForDisplay = (dateOfBirth: string) => {
  const parsedDate = dayjs(formatDateOfBirthForBooking(dateOfBirth));
  return parsedDate.isValid() ? parsedDate.format("MMM D, YYYY") : dateOfBirth;
};

interface ValidatedPatientFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "className" | "disabled" | "id" | "onChange" | "value"
  > {
  containerClassName?: string;
  error?: FieldError;
  id: string;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  isEditing: boolean;
  label: string;
  mask?: (value: string) => string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  registration: UseFormRegisterReturn;
  useDefaultValue?: boolean;
  value: string;
}

const ValidatedPatientField: React.FC<ValidatedPatientFieldProps> = ({
  children,
  containerClassName,
  error,
  id,
  inputRef,
  isEditing,
  label,
  mask,
  onChange,
  registration,
  useDefaultValue = false,
  value,
  ...inputProps
}) => {
  const visibleError = isEditing ? error : undefined;
  const errorId = visibleError ? `${id}-error` : undefined;

  return (
    <div className={containerClassName || styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className={formInputStyles.inputContainer}>
        <input
          {...registration}
          {...inputProps}
          id={id}
          ref={(element) => {
            if (isEditing) registration.ref(element);
            if (inputRef) inputRef.current = element;
          }}
          className={cx(styles.fieldInput, {
            [formInputStyles.error]: Boolean(visibleError),
          })}
          {...(useDefaultValue ? { defaultValue: value } : { value })}
          disabled={!isEditing}
          required
          aria-invalid={Boolean(visibleError)}
          aria-describedby={errorId}
          onChange={(event) => {
            if (mask) {
              event.target.value = mask(event.target.value);
            }
            onChange(event);
            registration.onChange(event);

            if (mask) {
              const validInputCharacters =
                event.currentTarget.value.match(/^[\d/\-()\s]+/);
              let cursorIndex = validInputCharacters?.[0].length || 0;

              if (
                (event.nativeEvent as InputEvent).inputType ===
                "deleteContentBackward"
              ) {
                if (
                  validInputCharacters?.[0].endsWith("/") ||
                  validInputCharacters?.[0].endsWith("-")
                ) {
                  cursorIndex -= 1;
                } else if (validInputCharacters?.[0].endsWith(") ")) {
                  cursorIndex -= 2;
                }
              }

              event.target.setSelectionRange(cursorIndex, cursorIndex);
            }
          }}
        />
        {children}
        {visibleError && (
          <img className={formInputStyles.errorIcon} src={errorIcon} alt="" />
        )}
      </div>
      {visibleError && (
        <p id={errorId} className={formInputStyles.errorMessage}>
          Error: {visibleError.message}
        </p>
      )}
    </div>
  );
};

export const KnownPatientScheduler: React.FC<KnownPatientSchedulerProps> = ({
  doctors,
  office,
  patient,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const location = useLocation();
  const knownPatientReturnToRef = React.useRef("");
  const trackedStepOneOfficeIdRef = React.useRef<string | null>(null);
  const { trackEvent } = useTrackEvent();
  const { currentAppointment } = useCurrentAppointment();
  const nameMaxLength = areExtendedPatientNameFieldsEnabled() ? 30 : 20;
  const initialPatientDetails = React.useMemo<KnownPatientFormDetails>(
    () => ({
      firstName: patient.firstName,
      lastName: patient.lastName,
      dateOfBirth: formatDateOfBirthForInput(patient.dateOfBirth),
      phoneNumber: maskPhone(patient.phoneNumber),
      email: patient.email,
    }),
    [
      patient.dateOfBirth,
      patient.email,
      patient.firstName,
      patient.lastName,
      patient.phoneNumber,
    ]
  );
  const [selectedSlot, setSelectedSlot] = React.useState<Slot | undefined>();
  const [reviewSlot, setReviewSlot] = React.useState<Slot | undefined>();
  const [isEditingPatient, setIsEditingPatient] = React.useState(false);
  const [patientDetails, setPatientDetails] = React.useState(
    initialPatientDetails
  );
  const {
    register,
    reset: resetPatientForm,
    trigger,
    formState: { errors: patientDetailErrors, isValid: patientDetailsValid },
  } = useForm<KnownPatientFormDetails>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialPatientDetails,
  });
  const [smsOptInNotices, setSmsOptInNotices] = React.useState(
    patient.smsOptInNotices
  );
  const [smsOptInPromos, setSmsOptInPromos] = React.useState(
    patient.smsOptInPromos
  );
  const dateOfBirthInputRef = React.useRef<HTMLInputElement>(null);
  const [newDateArr, setNewDateArr] = React.useState({});
  const [firstAvailDocs, setFirstAvailDocs] = React.useState({});
  const [showFeatureOffice, setShowFeatureOffice] = React.useState(false);
  const [mobileNearbyExpanded, setMobileNearbyExpanded] = React.useState(false);
  const [slotMetaData, setSlotMetaData] = React.useState({});
  const [hasRequestedAvailability, setHasRequestedAvailability] =
    React.useState(false);
  const [submitLocked, setSubmitLocked] = React.useState(false);
  const [searchDateOrNull] = useQueryParam("date", null);
  const [rslb] = useQueryParam("rslb", "false");
  const searchDate = searchDateOrNull || dayjs().format(API_DATE_FORMAT);
  const screenSize = useScreenSize();
  const isMobileLayout = screenSize < 992;
  const isSlotLoaded = useSelector((state: RootState) =>
    selectIstimeSlotLoaded(state)
  );
  const timeslots = useSelector((state: RootState) => selectTimeslots(state));
  const nearbyOffices = useSelector((state: RootState) =>
    selectNearbyOffices(state, office.id)
  );
  const nearbyOfficeAvailabilitySlots = useSelector(
    selectNearbyOfficeAvailabilitySlots
  );
  const isLoading = useSelector(selectNearbyOfficeLoadingState);
  const appointmentBookedStatus = useSelector(selectAppointmentBookedStatus);

  React.useEffect(() => {
    dispatch(setTimeSlots([]));
  }, [dispatch, office.id]);

  React.useEffect(() => {
    if (doctors.length === 0 || hasRequestedAvailability) return;

    const doctorIds = doctors.map((doctor) => doctor.id);
    dispatch(
      fetchOfficeAvailability({
        officeId: office.id,
        searchDate,
        examType: ExamType.Contacts,
        doctorIds,
        patientReturnStatus: PatientReturnStatus.Returning,
        version: 2,
        rsl: rslb === "false" ? true : false,
      })
    );

    const loadSlotMetaData = async () => {
      const startDate = dayjs().format(API_DATE_FORMAT);
      const endDate = dayjs(startDate).add(49, "day").format(API_DATE_FORMAT);
      try {
        if (rslb === "false") {
          const slotsMetaData = await getSlotMetaData({
            officeId: office.id,
            startDate,
            endDate,
            examType: ExamType.Contacts,
            doctorIds,
            patientReturnStatus: PatientReturnStatus.Returning,
            rsl: true,
          });
          setSlotMetaData(slotsMetaData);
        }
      } catch {
        setSlotMetaData({});
      }
    };

    loadSlotMetaData();
    setHasRequestedAvailability(true);
  }, [
    dispatch,
    doctors,
    hasRequestedAvailability,
    office.id,
    rslb,
    searchDate,
  ]);

  React.useEffect(() => {
    if (!isSlotLoaded) return;

    const nextDateArr = {};
    const firstAvailDoctors = {};
    const availabilityDoctorDetails = doctors.reduce<
      Record<string, string | null>
    >((details, doctor) => {
      details[doctor.id] = null;
      return details;
    }, {});
    const weekCalendar = Array.from({ length: 7 }).map((_, index) =>
      dayjs(searchDate).add(index, "day").format(API_DATE_FORMAT)
    );
    const nextWeekAvailDate = dayjs(searchDate).add(
      isMobileLayout ? 4 : 6,
      "day"
    );
    let weekSlotCount = 0;

    if (timeslots.length > 0) {
      const nowAsString = dayjs()
        .startOf("minute")
        .tz(timeslots[0].timeZoneName)
        .format("YYYY-MM-DD HH:mm:ss");

      timeslots.forEach((slotDetails) => {
        const doctorId = slotDetails.doctorId;
        if (!doctors.some((doctor) => doctor.id === doctorId)) return;
        if (nowAsString >= `${slotDetails.date} ${slotDetails.startTime}`) {
          return;
        }

        if (typeof nextDateArr[slotDetails.date] === "undefined") {
          nextDateArr[slotDetails.date] = {};
        }
        if (typeof nextDateArr[slotDetails.date][doctorId] === "undefined") {
          nextDateArr[slotDetails.date][doctorId] = [
            1,
            "available",
            slotDetails.date,
          ];
        } else {
          nextDateArr[slotDetails.date][doctorId][0]++;
        }

        if (typeof firstAvailDoctors[doctorId] === "undefined") {
          firstAvailDoctors[doctorId] = slotDetails;
        }

        if (
          nextWeekAvailDate.isBefore(slotDetails.date) &&
          (availabilityDoctorDetails[doctorId] === null ||
            dayjs(availabilityDoctorDetails[doctorId]).isAfter(
              slotDetails.date
            ))
        ) {
          availabilityDoctorDetails[doctorId] = slotDetails.date;
        }

        if (weekCalendar[6] >= slotDetails.date) {
          weekSlotCount += 1;
        }
      });
    }

    setNewDateArr(nextDateArr);
    setFirstAvailDocs(firstAvailDoctors);
    dispatch(
      setOfficeAvailability({
        officeId: office.id,
        body: {
          loaded: true,
          doctors: availabilityDoctorDetails,
        },
      })
    );
    if (weekSlotCount < 6) setShowFeatureOffice(true);
  }, [
    dispatch,
    doctors,
    isMobileLayout,
    isSlotLoaded,
    office.id,
    searchDate,
    timeslots,
  ]);

  React.useEffect(() => {
    if (trackedStepOneOfficeIdRef.current === office.id) return;
    if (
      doctors.length > 0 &&
      (!hasRequestedAvailability || !isSlotLoaded)
    ) {
      return;
    }

    const doctorsById = doctors.reduce<Record<string, Doctor>>(
      (doctorMap, doctor) => {
        doctorMap[doctor.id] = doctor;
        return doctorMap;
      },
      {}
    );
    const nowAsString = timeslots.length
      ? dayjs()
          .startOf("minute")
          .tz(timeslots[0].timeZoneName)
          .format("YYYY-MM-DD HH:mm:ss")
      : "";
    const upcomingSlots = timeslots.filter(
      (slot) =>
        doctorsById[slot.doctorId] &&
        nowAsString < `${slot.date} ${slot.startTime}`
    );
    const firstAvailableDate = upcomingSlots
      .map((slot) => slot.date)
      .sort()[0];
    const weekEnd = dayjs(searchDate).add(6, "day").format(API_DATE_FORMAT);
    const firstAvailableDateSlots =
      firstAvailableDate && firstAvailableDate <= weekEnd
        ? upcomingSlots.filter((slot) => slot.date === firstAvailableDate)
        : [];
    const vehSlots = firstAvailableDateSlots.filter(
      (slot) => doctorsById[slot.doctorId].offersVEH
    ).length;

    trackEvent("step-1-scheduler-enter", {
      location: office.id,
      logicalId: office.logicalId,
      office_type: office.offersVEH ? "VEH" : "Traditional",
      total_slots: firstAvailableDateSlots.length,
      veh_slots: vehSlots,
      traditional_slots: firstAvailableDateSlots.length - vehSlots,
      SVT: "KP",
    });
    trackedStepOneOfficeIdRef.current = office.id;
  }, [
    doctors,
    hasRequestedAvailability,
    isSlotLoaded,
    office.id,
    office.logicalId,
    office.offersVEH,
    searchDate,
    timeslots,
    trackEvent,
  ]);

  React.useEffect(() => {
    dispatch(
      fetchNearbyOffices({
        officeId: office.id,
        examType: ExamType.Contacts,
        patientReturnStatus: PatientReturnStatus.Returning,
      })
    );
  }, [dispatch, office.id]);

  const selectedDoctor = reviewSlot
    ? doctors.find((doctor) => doctor.id === reviewSlot.doctorId)
    : undefined;
  const discountVEHDoctor = doctorDiscountNameVEH();
  const isAnonymizedVEHDoctor =
    selectedDoctor?.offersVEH &&
    selectedDoctor?.firstName.toLowerCase() === "open network";
  const reviewDoctorName = selectedDoctor
    ? selectedDoctor.offersVEH && office.discountVEH && discountVEHDoctor
      ? doctorDiscountNameVEH()
      : doctorFullName(selectedDoctor)
    : "Selected doctor";
  const reviewDate = reviewSlot
    ? dayjs(reviewSlot.date).format("ddd, MMM D, YYYY")
    : "";
  const reviewTime = reviewSlot
    ? dayjs(`${reviewSlot.date} ${reviewSlot.startTime}`).format("h:mm A")
    : "";

  const handleSlotSelection = (_: unknown, slot: Slot) => {
    const slotDoctor = doctors.find((doctor) => doctor.id === slot.doctorId);

    setSelectedSlot(slot);
    if (!isMobileLayout) setReviewSlot(slot);

    if (slotDoctor) {
      trackEvent("step-5-selected-appointment-time", {
        location: office.id,
        logicalId: office.logicalId,
        selected_type: ExamType.Contacts,
        slot_type: slotDoctor.offersVEH ? "VEH" : "Traditional",
        patient_type: PatientReturnStatus.Returning,
        doctorId: slotDoctor.id,
      });
    }
  };

  const reviewSelectedMobileSlot = () => {
    if (selectedSlot) setReviewSlot(selectedSlot);
  };

  const resetPatientDetails = React.useCallback(() => {
    setPatientDetails(initialPatientDetails);
    resetPatientForm(initialPatientDetails);
  }, [initialPatientDetails, resetPatientForm]);

  React.useEffect(() => {
    if (!reviewSlot) return;

    setIsEditingPatient(false);
    resetPatientDetails();
    setSmsOptInNotices(patient.smsOptInNotices);
    setSmsOptInPromos(patient.smsOptInPromos);
  }, [
    patient.smsOptInNotices,
    patient.smsOptInPromos,
    resetPatientDetails,
    reviewSlot,
  ]);

  const setPatientField = (
    field: keyof typeof patientDetails,
    value: string
  ) => {
    setPatientDetails((current) => ({ ...current, [field]: value }));
  };

  const firstName = register(
    "firstName",
    presenceNameValidation(
      "Not a valid first name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const lastName = register(
    "lastName",
    presenceNameValidation(
      "Not a valid last name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const phoneNumber = register("phoneNumber", {
    validate: isValidPhoneNumber,
  });
  const email = register("email", {
    required: "Not a valid email",
    validate: {
      isEmail: (value) => isEmail(value),
      isValidTLDLength: (value) => isValidTLDLength(value),
      acceptableCharacters: (value) => acceptableCharacters(value),
    },
  });
  const dateOfBirth = register("dateOfBirth", {
    validate: (value) => {
      if (!selectedDoctor?.offersVEH) {
        return isValidDateOfBirth(value);
      }

      return office.ageRestrictVEH
        ? isValidDOBRestrictVEH(value)
        : isValidDateOfBirthVEH(value);
    },
  });

  const completeBooking = () => {
    if (
      !reviewSlot ||
      !selectedDoctor ||
      submitLocked ||
      (isEditingPatient && !patientDetailsValid)
    ) {
      if (isEditingPatient) void trigger();
      return;
    }

    const bookingPatient = {
      ...patient,
      ...patientDetails,
      dateOfBirth: formatDateOfBirthForBooking(patientDetails.dateOfBirth),
      phoneNumber: patientDetails.phoneNumber.replace(/\D/g, ""),
      smsOptInNotices,
      smsOptInPromos,
    };

    knownPatientReturnToRef.current = `${location.pathname}${location.search}${location.hash}`;
    setSubmitLocked(true);
    dispatch(resetAppointmentBooked());
    dispatch(
      setAppointmentData({
        status: true,
        office,
        doctor: selectedDoctor,
        timeSlot: reviewSlot,
        patientType: PatientReturnStatus.Returning,
        eyeExam: ExamType.Contacts,
        patientDetails: bookingPatient,
        lastRequestUrl: searchDate,
      })
    );
    dispatch(
      createAppointment({
        officeId: office.id,
        officeUid: office.officeUid,
        providerId: selectedDoctor.provider_id || selectedDoctor.id,
        patient: bookingPatient,
        doctorId: reviewSlot.doctorId,
        date: reviewSlot.date,
        startTime: reviewSlot.startTime,
        endTime: reviewSlot.endTime,
        examType: ExamType.Contacts,
        patientReturnStatus: PatientReturnStatus.Returning,
        allowReschedule: Boolean(currentAppointment),
      })
    );
  };

  React.useEffect(() => {
    if (!submitLocked) return;

    switch (appointmentBookedStatus) {
      case CreateAppointmentResult.Created:
        history.push(paths.appointmentSuccessNewV2({ officeId: office.id }));
        break;
      case CreateAppointmentResult.SlotTaken:
        history.push(
          paths.appointmentsSlotTaken({
            officeId: office.id,
            examType: ExamType.Contacts,
            patientReturnStatus: PatientReturnStatus.Returning,
          }),
          knownPatientSlotTakenState(knownPatientReturnToRef.current)
        );
        break;
      case CreateAppointmentResult.Failed:
      case CreateAppointmentResult.DuplicateAppointment:
        history.push(paths.appointmentsFailureV2({ officeId: office.id }));
        break;
    }
  }, [appointmentBookedStatus, history, office.id, submitLocked]);

  const selectedSlotDoctor = selectedSlot
    ? doctors.find((doctor) => doctor.id === selectedSlot.doctorId)
    : undefined;
  const sortedNearbyOffices = [...nearbyOffices].sort((a, b) => {
    const aTotal = nearbyOfficeAvailabilitySlots[a.id]?.total || 0;
    const bTotal = nearbyOfficeAvailabilitySlots[b.id]?.total || 0;
    return bTotal - aTotal;
  });
  const promotedFeatureOffices = sortedNearbyOffices.filter(
    (nearby) => !nearby.doNotPromote
  );
  const featureOffice =
    promotedFeatureOffices.length > 0 ? promotedFeatureOffices[0] : null;
  const availabilityContent = (
    <WeekTimeSlots
      key="weektime1"
      office={office}
      featureOffice={featureOffice}
      showFeatureOffice={showFeatureOffice}
      doctors={doctors}
      searchDate={searchDate}
      onSelection={handleSlotSelection}
      seeMoreScrollToTop={() => undefined}
      selectedSlot={selectedSlot}
      isEditForm={false}
      setTimeslotsExecution={() => setHasRequestedAvailability(false)}
      newDateArr={newDateArr}
      pageSection="TIMESLOT"
      newDesign="true"
      slotMetaData={slotMetaData}
      screenSize={screenSize}
      firstAvailDocs={firstAvailDocs}
      useClassicStartLayout={false}
      headerContent={
        isMobileLayout ? undefined : <WelcomeBack patient={patient} />
      }
      hideHeading={!isMobileLayout}
      enableMobileDateScroller
      isMobileView={isMobileLayout}
      onNextMobile={reviewSelectedMobileSlot}
    />
  );

  return (
    <>
      <LoadingToast isLoading={submitLocked} />
      {isMobileLayout ? (
        <div className={styles.mobileSchedule}>
          <div
            className={cx(styles.mobileIntroCard, {
              [styles.mobileIntroCardExpanded]: mobileNearbyExpanded,
            })}
          >
            <OfficeCard
              office={office}
              selectedSlot={selectedSlot}
              isEditForm={false}
              compact
              compactActionLabel={
                mobileNearbyExpanded ? "Hide offices" : "Change"
              }
              onChange={() => setMobileNearbyExpanded((expanded) => !expanded)}
            />
            {mobileNearbyExpanded && (
              <>
                <TrustBuildCard />
                <div className={styles.mobileNearbyHeading}>
                  More nearby offices
                </div>
                <NearbyOffice
                  nearbyOffices={sortedNearbyOffices}
                  availabilitySlots={nearbyOfficeAvailabilitySlots}
                  patientReturnStatus={PatientReturnStatus.Returning}
                  examType={ExamType.Contacts}
                  isMobile
                  isLoading={isLoading}
                  newDesign="true"
                  screenSize={screenSize}
                  useClassicStartLayout={false}
                  expanded
                  onExpandedChange={setMobileNearbyExpanded}
                  showExpandedToggle={false}
                />
              </>
            )}
            <WelcomeBack patient={patient} mobile />
          </div>
          {availabilityContent}
        </div>
      ) : (
        <div className={startStyles.firstContainer}>
          {availabilityContent}
          <aside className={startStyles.rightContainer}>
            <OfficeCard
              office={office}
              selectedSlot={selectedSlot}
              isEditForm={false}
            />
            <TrustBuildCard />
            {selectedSlotDoctor && selectedSlot && (
              <DoctorCard
                doctor={selectedSlotDoctor}
                selectedOffice={office}
                slot={selectedSlot}
                searchDate={searchDate}
                onClick={() => {
                  setSelectedSlot(undefined);
                  setReviewSlot(undefined);
                }}
                isEditForm={false}
              />
            )}
            {selectedSlot === undefined && (
              <NearbyOffice
                nearbyOffices={sortedNearbyOffices}
                availabilitySlots={nearbyOfficeAvailabilitySlots}
                onClick={() => undefined}
                patientReturnStatus={PatientReturnStatus.Returning}
                examType={ExamType.Contacts}
                isEditForm={false}
                isMobile={false}
                isLoading={isLoading}
                newDesign="true"
                useClassicStartLayout={false}
              />
            )}
          </aside>
        </div>
      )}
      <Footer
        formattedPhone={office.formattedPhone}
        showSMSContent={true}
        useV3StartPageCopy={true}
      />
      <ReviewModal
        isOpen={Boolean(reviewSlot)}
        onRequestClose={() => setReviewSlot(undefined)}
        closeOnOverlayClick
        hideClose
        className={styles.reviewModal}
        contentclassName={styles.reviewModalContent}
        overlayClassName={styles.reviewOverlay}
      >
        {reviewSlot && (
          <section
            className={styles.reviewContent}
            aria-labelledby="known-patient-review-title"
          >
            <header className={styles.reviewHeader}>
              <h1 id="known-patient-review-title">
                <span className={styles.reviewTitlePrimary}>Almost there!</span>{" "}
                <span className={styles.reviewTitleSupporting}>
                  Does this look correct?
                </span>
              </h1>
              <button
                type="button"
                className={styles.modalCloseButton}
                aria-label="Close appointment review"
                onClick={() => setReviewSlot(undefined)}
              >
                <img src={closeIcon} alt="" />
              </button>
            </header>

            <div className={styles.reviewBody}>
              <section>
                <div className={styles.reviewSectionHeader}>
                  <div className={styles.panelTitle}>Location & time</div>
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => setReviewSlot(undefined)}
                  >
                    <img src={editIcon} alt="" />
                    Change
                  </button>
                </div>
                <div className={styles.appointmentSummary}>
                  <div
                    className={cx(styles.summaryRow, styles.summaryDoctor, {
                      [styles.summaryDoctorVEH]: isAnonymizedVEHDoctor,
                    })}
                  >
                    <img
                      src={selectedDoctor?.headshotUrl || doctorPlaceholder}
                      alt=""
                      className={
                        isAnonymizedVEHDoctor
                          ? styles.doctorThumbVEH
                          : styles.doctorThumb
                      }
                    />
                    <strong>{reviewDoctorName}</strong>
                  </div>
                  <div
                    className={`${styles.summaryRow} ${styles.summaryLocation}`}
                  >
                    <img
                      className={`${styles.summaryGraphic} ${styles.locationGraphic}`}
                      src={locationIcon}
                      alt=""
                    />
                    <div className={styles.addressBlock}>
                      <strong>{office.displayName}</strong>
                      <span>{office.address1}</span>
                      <span>
                        {office.city}, {office.stateAbbr}{" "}
                        {office.formattedZipCode}
                      </span>
                      <span>{office.formattedPhone}</span>
                    </div>
                  </div>
                  <div className={`${styles.summaryRow} ${styles.summaryDate}`}>
                    <img
                      className={styles.summaryGraphic}
                      src={calendarIcon}
                      alt=""
                    />
                    <span>{reviewDate}</span>
                  </div>
                  <div className={`${styles.summaryRow} ${styles.summaryTime}`}>
                    <img
                      className={styles.summaryGraphic}
                      src={clockIcon}
                      alt=""
                    />
                    <span>{reviewTime}</span>
                  </div>
                </div>
              </section>

              <section className={styles.personalDetails}>
                <div className={styles.panelTitle}>Personal details</div>
                <div className={styles.personalQuestionGroup}>
                  <p className={styles.question}>
                    Has your name, email, or phone number changed since your
                    last visit?
                  </p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioRow}>
                      <input
                        type="radio"
                        name="known-patient-details-changed"
                        checked={!isEditingPatient}
                        onChange={() => {
                          setIsEditingPatient(false);
                          resetPatientDetails();
                        }}
                      />
                      No, nothing has changed
                    </label>
                    <label className={styles.radioRow}>
                      <input
                        type="radio"
                        name="known-patient-details-changed"
                        checked={isEditingPatient}
                        onChange={() => {
                          setIsEditingPatient(true);
                          void trigger();
                        }}
                      />
                      Yes, I need to update
                    </label>
                  </div>
                </div>

                <div className={styles.desktopDetailsGrid}>
                  <ValidatedPatientField
                    id="known-first-name"
                    label="First Name*"
                    registration={firstName}
                    error={patientDetailErrors.firstName}
                    isEditing={isEditingPatient}
                    value={patientDetails.firstName}
                    maxLength={nameMaxLength}
                    onChange={(event) => {
                      event.target.value = sanitizePatientNameInput(
                        event.target.value,
                        nameMaxLength
                      );
                      setPatientField("firstName", event.target.value);
                    }}
                  />
                  <ValidatedPatientField
                    id="known-last-name"
                    label="Last Name*"
                    registration={lastName}
                    error={patientDetailErrors.lastName}
                    isEditing={isEditingPatient}
                    value={patientDetails.lastName}
                    maxLength={nameMaxLength}
                    onChange={(event) => {
                      event.target.value = sanitizePatientNameInput(
                        event.target.value,
                        nameMaxLength
                      );
                      setPatientField("lastName", event.target.value);
                    }}
                  />
                  <ValidatedPatientField
                    key={
                      isEditingPatient
                        ? "known-date-of-birth-edit"
                        : "known-date-of-birth-view"
                    }
                    id="known-date-of-birth"
                    label="Date of Birth*"
                    registration={dateOfBirth}
                    error={patientDetailErrors.dateOfBirth}
                    isEditing={isEditingPatient}
                    value={
                      isEditingPatient
                        ? patientDetails.dateOfBirth
                        : formatDateOfBirthForDisplay(
                            patientDetails.dateOfBirth
                          )
                    }
                    type="text"
                    inputMode="numeric"
                    placeholder="mm/dd/yyyy"
                    mask={maskDate}
                    useDefaultValue={isEditingPatient}
                    inputRef={dateOfBirthInputRef}
                    containerClassName={`${styles.field} ${styles.dateField}`}
                    onChange={(event) =>
                      setPatientField("dateOfBirth", event.target.value)
                    }
                  >
                    <button
                      type="button"
                      className={styles.dateInputIcon}
                      aria-label="Open date of birth calendar"
                      disabled={!isEditingPatient}
                      onClick={() => {
                        dateOfBirthInputRef.current?.focus();
                      }}
                    >
                      <img src={calendarIcon} alt="" />
                    </button>
                  </ValidatedPatientField>
                  <ValidatedPatientField
                    id="known-phone-number"
                    label="Phone Number*"
                    registration={phoneNumber}
                    error={patientDetailErrors.phoneNumber}
                    isEditing={isEditingPatient}
                    value={patientDetails.phoneNumber}
                    type="tel"
                    inputMode="numeric"
                    placeholder="(___) ___-____"
                    mask={maskPhone}
                    onChange={(event) => {
                      setPatientField("phoneNumber", event.target.value);
                    }}
                  />
                  <ValidatedPatientField
                    id="known-email"
                    label="Email Address*"
                    registration={email}
                    error={patientDetailErrors.email}
                    isEditing={isEditingPatient}
                    value={patientDetails.email}
                    type="email"
                    containerClassName={styles.fieldWide}
                    onChange={(event) =>
                      setPatientField("email", event.target.value)
                    }
                  />
                </div>

                <div className={styles.desktopPreferences}>
                  <SmsCheckbox
                    label="I'd like to receive text reminders about my appointment, about my prescription, and when my glasses are ready. (MyEyeDr. Notices)*"
                    checked={smsOptInNotices}
                    onChange={(event) =>
                      setSmsOptInNotices(event.target.checked)
                    }
                  />
                  <SmsCheckbox
                    label="I'd like to receive promotional texts with eyewear and eyecare offers. (MyEyeDr. Promos)*"
                    checked={smsOptInPromos}
                    onChange={(event) =>
                      setSmsOptInPromos(event.target.checked)
                    }
                  />
                </div>
              </section>
            </div>

            <footer className={styles.reviewFooter}>
              <Button
                text="Complete Booking"
                size="large"
                role={ButtonRole.Standard}
                onClick={completeBooking}
                disabled={
                  submitLocked || (isEditingPatient && !patientDetailsValid)
                }
                loading={submitLocked}
                className={styles.primaryButton}
              />
            </footer>
          </section>
        )}
      </ReviewModal>
    </>
  );
};

interface KnownPatientLoadedProps {
  officeIdForScheduling: string;
  patient: KnownPatient;
}

interface KnownPatientOfficeContentProps {
  doctors: Doctor[];
  office: Office;
  onLoaded: () => void;
  patient: KnownPatient;
}

const KnownPatientOfficeContent: React.FC<KnownPatientOfficeContentProps> = ({
  doctors,
  office,
  onLoaded,
  patient,
}) => {
  React.useEffect(() => {
    onLoaded();
  }, [onLoaded]);

  return (
    <>
      <KnownPatientScheduler
        office={office}
        doctors={doctors}
        patient={patient}
      />
    </>
  );
};

const KnownPatientLoaded: React.FC<KnownPatientLoadedProps> = ({
  officeIdForScheduling,
  patient,
}) => {
  const [isOfficeReady, setIsOfficeReady] = React.useState(false);
  const navbarOffice = useSelector((state: RootState) =>
    selectOffice(state, officeIdForScheduling)
  );

  return (
    <Layout
      fullContainer
      scheduleBanner
      navbar={
        navbarOffice && navbarOffice !== "not-found" ? (
          <Navbar office={navbarOffice} />
        ) : undefined
      }
    >
      {!isOfficeReady && <KnownPatientScheduleLoading />}
      <div className={styles.knownPatientSchedule} hidden={!isOfficeReady}>
        <div className={styles.scheduleShell}>
          <WithOffice officeId={officeIdForScheduling} version={1}>
            {({ office, officeId }) => (
              <WithOfficeDoctors officeId={officeId}>
                {({ doctors }) => (
                  <KnownPatientOfficeContent
                    key={officeId}
                    office={office}
                    doctors={doctors}
                    onLoaded={() => setIsOfficeReady(true)}
                    patient={patient}
                  />
                )}
              </WithOfficeDoctors>
            )}
          </WithOffice>
        </div>
      </div>
    </Layout>
  );
};

export const KnownPatientPage: React.FC = () => {
  const history = useHistory();
  const { officeId: routeOfficeId } = useParams<{ officeId?: string }>();
  const { currentPatient, fetchCurrentPatient, sessionStatus } =
    useCurrentPatient();
  const [status, setStatus] = React.useState<KnownPatientPageStatus>("loading");
  const [knownPatient, setKnownPatient] = React.useState<KnownPatient | null>(
    null
  );
  const [lastAppointment, setLastAppointment] =
    React.useState<CurrentAppointment | null>(null);
  const [hasLoadError, setHasLoadError] = React.useState(false);

  React.useEffect(() => {
    if (
      !currentPatient &&
      (sessionStatus === "uninitialized" || sessionStatus === "logged-in")
    ) {
      fetchCurrentPatient();
    }
  }, [currentPatient, fetchCurrentPatient, sessionStatus]);

  const loadSupplementalKnownPatient = React.useCallback(async () => {
    try {
      const result = await getKnownPatientRequest();
      setKnownPatient(result);
    } catch {
      setHasLoadError(true);
    }
  }, []);

  React.useEffect(() => {
    loadSupplementalKnownPatient();
  }, [loadSupplementalKnownPatient]);

  React.useEffect(() => {
    let mounted = true;

    const loadLastAppointment = async () => {
      try {
        const result = await getLastAppointmentRequest();
        if (mounted) setLastAppointment(result);
      } catch {
        if (mounted) setLastAppointment(null);
      }
    };

    loadLastAppointment();

    return () => {
      mounted = false;
    };
  }, []);

  const appointmentForDisplay = lastAppointment;
  const patient = currentPatient
    ? fallbackKnownPatient({
        ...knownPatient,
        ...currentPatient,
        phoneNumber: currentPatient.phoneNumber || knownPatient?.phoneNumber,
        email: currentPatient.email || knownPatient?.email,
        smsOptInNotices:
          currentPatient.smsOptInNotices ?? knownPatient?.smsOptInNotices,
        smsOptInPromos:
          currentPatient.smsOptInPromos ?? knownPatient?.smsOptInPromos,
        lastAppointment:
          appointmentToKnownPatientLastAppointment(appointmentForDisplay) ||
          knownPatient?.lastAppointment ||
          null,
      })
    : knownPatient;

  React.useEffect(() => {
    if (hasLoadError && !currentPatient) {
      setStatus("error");
      return;
    }

    if (
      (sessionStatus === "expired" || sessionStatus === "logged-out") &&
      !knownPatient
    ) {
      setStatus("empty");
      return;
    }

    if (!currentPatient && !knownPatient) {
      setStatus("loading");
      return;
    }

    setStatus("resolved");
  }, [
    currentPatient,
    hasLoadError,
    knownPatient,
    sessionStatus,
  ]);

  const officeIdForScheduling = routeOfficeId;

  const lastAppointmentOfficeId =
    appointmentForDisplay?.officeId || patient?.lastAppointment?.officeId;
  const { office: lastAppointmentOffice } = useOffice(
    lastAppointmentOfficeId as string
  );
  const discountVEHDoctor = doctorDiscountNameVEH();
  const officeOffersDiscountVEH =
    lastAppointmentOffice?.discountVEH && discountVEHDoctor;

  const doctorName = appointmentForDisplay?.doctor
    ? appointmentForDisplay.doctor.firstName.toLowerCase() ===
        "open network" && officeOffersDiscountVEH
      ? doctorDiscountNameVEH()
      : doctorFullName(appointmentForDisplay.doctor)
    : patient?.lastAppointment?.doctorFirstName &&
      patient?.lastAppointment?.doctorLastName
    ? patient.lastAppointment.doctorFirstName.toLowerCase() ===
        "open network" && officeOffersDiscountVEH
      ? doctorDiscountNameVEH()
      : doctorFullName({
          id: "known-patient-page-last-doctor",
          firstName: patient.lastAppointment.doctorFirstName,
          lastName: patient.lastAppointment.doctorLastName,
          providerSuffix:
            patient.lastAppointment.doctorProviderSuffix || undefined,
        })
    : "Your MyEyeDr. doctor";

  const appointmentDate = appointmentForDisplay?.date
    ? dayjs(appointmentForDisplay.date).format("MMM D, YYYY")
    : patient?.lastAppointment?.date
    ? dayjs(patient.lastAppointment.date).format("MMM D, YYYY")
    : "Not available";

  const appointmentTime = appointmentForDisplay?.startTime
    ? dayjs(
        `${appointmentForDisplay.date} ${appointmentForDisplay.startTime}`
      ).format("h:mm A")
    : null;

  if (status === "empty") {
    return <Redirect to={paths.root()} />;
  }

  if (status === "resolved" && patient && officeIdForScheduling) {
    return (
      <KnownPatientLoaded
        officeIdForScheduling={officeIdForScheduling}
        patient={patient}
      />
    );
  }

  return (
    <>
      {status === "loading" && (
        <Layout fullContainer scheduleBanner>
          <KnownPatientScheduleLoading />
        </Layout>
      )}

      {status === "resolved" && patient && !officeIdForScheduling && (
        <Layout scheduleBanner>
          <div className={styles.container}>
            <div className={styles.pageGrid}>
              <div className={styles.primaryColumn}>
                <section
                  className={styles.summaryCard}
                  aria-label="Known patient details"
                >
                  <WelcomeBack patient={patient} />
                  <div className={styles.summaryPanel}>
                    <div className={styles.panelTitle}>Last appointment</div>
                    <dl className={styles.detailList}>
                      <div>
                        <dt>Doctor</dt>
                        <dd>{doctorName}</dd>
                      </div>
                      <div>
                        <dt>Date</dt>
                        <dd>
                          {appointmentDate}
                          {appointmentTime ? ` at ${appointmentTime}` : null}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <Button
                    text="Choose an office"
                    size="large"
                    role={ButtonRole.Standard}
                    onClick={() => history.push(paths.offices())}
                    className={styles.primaryButton}
                  />
                </section>
              </div>

              <aside
                className={styles.detailsColumn}
                aria-label="Patient information"
              >
                <section className={styles.infoCard}>
                  <h2>Patient information</h2>
                  <dl className={styles.detailList}>
                    <div>
                      <dt>First Name</dt>
                      <dd>{patient.firstName || "Not available"}</dd>
                    </div>
                    <div>
                      <dt>Last Name</dt>
                      <dd>{patient.lastName || "Not available"}</dd>
                    </div>
                    <div>
                      <dt>Date of Birth</dt>
                      <dd>
                        {patient.dateOfBirth
                          ? dayjs(patient.dateOfBirth).format("MMM D, YYYY")
                          : "Not available"}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className={styles.infoCard}>
                  <h2>Contact information</h2>
                  <dl className={styles.detailList}>
                    <div>
                      <dt>Phone Number</dt>
                      <dd>
                        {patient.phoneNumber
                          ? maskPhone(patient.phoneNumber)
                          : "Not available"}
                      </dd>
                    </div>
                    <div>
                      <dt>Email Address</dt>
                      <dd>{patient.email || "Not available"}</dd>
                    </div>
                  </dl>
                </section>
              </aside>
            </div>
          </div>
        </Layout>
      )}

      {status === "error" && (
        <Layout scheduleBanner>
          <div className={styles.container}>
            <div className={styles.stateCard}>
              <h1 className={styles.stateTitle}>We hit a temporary issue</h1>
              <p className={styles.stateBody}>
                We were unable to load your known patient details. Please try
                again.
              </p>
              <div className={styles.actions}>
                <Link
                  to={history.location.pathname}
                  onClick={() => {
                    setHasLoadError(false);
                    setAppointmentLookupComplete(false);
                    fetchCurrentPatient();
                    loadSupplementalKnownPatient();
                  }}
                  className={styles.link}
                >
                  Try again
                </Link>
              </div>
            </div>
          </div>
        </Layout>
      )}
    </>
  );
};
